var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3, _react_refresh_temp_4;
_react_refresh_temp_3 = $RefreshSig$();
_react_refresh_temp_4 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateInvitationLink } from '@lucidlink/sdk';
import { BadRequestError } from '@lucidlink/sdk/fetcher';
import { Box, Modal, translate, useToastService } from '@lucidlink/uicore';
import { FormRoot, mergeNestedKeyErrorsToParentKey, transformServerErrorsToInjectableErrors, useFormRoot, } from '@lucidlink/uicore/form-builder';
import { useCopyToClipboardWithToast, useInvitationBaseUrl } from '#hooks';
import { useCreateIndividualInvitation, useCreateOrganizationInvitation, useGetOrganizationInvitation, useListWorkspaceDomains, } from '#query-hooks';
import { workspaceRoutes } from '#services';
import { ZeroKnowledgeLabel } from '../components/ZeroKnowledgeLabel';
import { INVITE_MEMBERS_FIELD_NAMES, INVITE_MEMBERS_STEPS, } from './constants/invite-members.constants';
import { CreateInvite, Success } from './steps';
// Wrapper component around the steps, handles the mutation success / error logic
// Avoids the need for additional context to share the mutation between the steps
// or probing the MutationCache which could be prone to bugs.
const InviteMembersCreateSteps = ({ closeModal, shouldShowSecondaryActionOnSuccess = false, titleId, workspaceData, }) => {
    _react_refresh_temp_3();
    const { addToast } = useToastService();
    const { copyToClipboard } = useCopyToClipboardWithToast();
    const invitationBaseUrl = useInvitationBaseUrl();
    const navigate = useNavigate();
    const { methods: { injectErrors }, setWizardStep, } = useFormRoot();
    const { query: { data: getOrganizationInvitationData, isLoading: isLoadingGetOrganizationInvitation, }, } = useGetOrganizationInvitation(workspaceData.id);
    const createIndividualInvitationErrorHandler = useCallback((error) => {
        if (error instanceof BadRequestError) {
            const errorsToInject = transformServerErrorsToInjectableErrors(error.errors);
            const transformedErrorsWithMergedEmailFiltersErrors = mergeNestedKeyErrorsToParentKey(INVITE_MEMBERS_FIELD_NAMES.emailFilters, errorsToInject);
            injectErrors(transformedErrorsWithMergedEmailFiltersErrors);
        }
    }, [injectErrors]);
    const { mutation: { data: createIndividualInvitationData, isPending: isPendingCreateIndividualInvitation, isSuccess: isSuccessCreateIndividualInvitation, mutate: createIndividualInvitation, }, } = useCreateIndividualInvitation(workspaceData.id, {
        onError: createIndividualInvitationErrorHandler,
    });
    const { mutation: { data: organizationInvitationData, error: organizationInvitationError, isError: isErrorOrganizationInvitation, isPending: isPendingOrganizationInvitation, isSuccess: isSuccessOrganizationInvitation, mutate: createOrganizationInvitation, }, } = useCreateOrganizationInvitation(workspaceData.id);
    const { query: { data: workspaceDomainsData }, } = useListWorkspaceDomains(workspaceData.id);
    useEffect(() => {
        if ((isSuccessCreateIndividualInvitation &&
            createIndividualInvitationData) ||
            (isSuccessOrganizationInvitation && organizationInvitationData)) {
            setWizardStep(INVITE_MEMBERS_STEPS.SUCCESS);
        }
    }, [
        createIndividualInvitationData,
        isSuccessCreateIndividualInvitation,
        isSuccessOrganizationInvitation,
        organizationInvitationData,
        setWizardStep,
    ]);
    const handledOrganizationInvitationErrors = useMemo(() => {
        if (isErrorOrganizationInvitation &&
            organizationInvitationError instanceof BadRequestError) {
            return organizationInvitationError.errors;
        }
        else {
            return null;
        }
    }, [isErrorOrganizationInvitation, organizationInvitationError]);
    const copyInvitationToClipboard = useCallback((data) => {
        const invitationUrl = generateInvitationLink(invitationBaseUrl, data).href;
        void copyToClipboard(invitationUrl, {
            onSuccess: () => {
                addToast({
                    'data-testid': 'copy-invite-success',
                    kind: 'positive',
                    content: {
                        label: translate('Invite link has been copied to your clipboard'),
                        message: translate('Share it with your teammates.'),
                    },
                });
            },
        });
    }, [addToast, copyToClipboard, invitationBaseUrl]);
    const onNavigateToWorkspaceMembers = useCallback(() => {
        void navigate(workspaceRoutes.workspaceSettingsMembers(workspaceData.id));
        closeModal();
    }, [closeModal, navigate, workspaceData.id]);
    return (_jsxs(_Fragment, { children: [_jsx(CreateInvite, { createIndividualInvitation: createIndividualInvitation, createOrganizationInvitation: createOrganizationInvitation, existingOrganizationInvitation: getOrganizationInvitationData, hasOrganizationDomain: Boolean(workspaceDomainsData && workspaceDomainsData.length > 0), isLoadingGetOrganizationInvitation: isLoadingGetOrganizationInvitation, isPendingMutation: isPendingCreateIndividualInvitation ||
                    isPendingOrganizationInvitation, onCopyToClipboard: copyInvitationToClipboard, organizationInvitationErrors: handledOrganizationInvitationErrors, titleId: titleId, workspaceData: workspaceData }), _jsx(Success, { data: createIndividualInvitationData || organizationInvitationData, onCopyToClipboard: copyInvitationToClipboard, onNavigateToWorkspaceMembers: onNavigateToWorkspaceMembers, shouldShowSecondaryActionOnSuccess: shouldShowSecondaryActionOnSuccess, titleId: titleId, workspaceId: workspaceData.id })] }));
};
_react_refresh_temp_1 = InviteMembersCreateSteps;
$RefreshReg$(_react_refresh_temp_1, "InviteMembersCreateSteps");
_react_refresh_temp_3(InviteMembersCreateSteps, "68qQfGlbWX2GyMMDSBgUTWD2MhQ=", false, () => [useToastService, useCopyToClipboardWithToast, useInvitationBaseUrl, useNavigate, useFormRoot, useGetOrganizationInvitation, useCreateIndividualInvitation, useCreateOrganizationInvitation, useListWorkspaceDomains]);
export const InviteMembersByEmailModal = ({ closeModal, shouldShowSecondaryActionOnSuccess = true, workspaceData, }) => {
    _react_refresh_temp_4();
    const titleId = useId();
    return (_jsx(Modal, { "aria-labelledby": titleId, closeModal: closeModal, modalLabel: _jsx(ZeroKnowledgeLabel, {}), modalWidth: "l", shouldCloseOnOverlayClick: false, children: _jsx(FormRoot, { "data-testid": "invite-members", initialWizardStep: INVITE_MEMBERS_STEPS.CREATE_INVITE, isFullHeight: true, children: _jsx(Box, { isFullHeight: true, orientation: "vertical", children: _jsx(InviteMembersCreateSteps, { closeModal: closeModal, shouldShowSecondaryActionOnSuccess: shouldShowSecondaryActionOnSuccess, titleId: titleId, workspaceData: workspaceData }) }) }) }));
};
_react_refresh_temp_2 = InviteMembersByEmailModal;
$RefreshReg$(_react_refresh_temp_2, "InviteMembersByEmailModal");
_react_refresh_temp_4(InviteMembersByEmailModal, "NDoDTbO7NND5BSV8uErVhNjxj+4=");
